import { computed, DestroyRef, effect, inject, signal, untracked, Service } from '@angular/core';
import { catchError, finalize, Observable, of, Subscription, tap } from 'rxjs';
import { Api } from '@api/api';
import { initCart as initCartFn } from '@api/functions';
import type {
  CartItemDto,
  CartRequestDto,
  CartResponseDto,
  OrderPricesDto,
  PopulatedCartItemDto,
  ProductListItemDto,
} from '@api/models';
import { vibrate } from '../helpers/vibrate.function';
import { CustomerService } from './customer.service';
import { GoogleAnalyticsService, ProductAnalyticsData } from './google-analytics.service';
import { ProductService } from './product.service';
import { WishlistService } from './wishlist.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PromoCodeService } from '../pages/checkout-page/components/promo-code/promo-code.service';
import { OrderService } from '../pages/checkout-page/order.service';
import { DeviceService } from './device-detector/device.service';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

/**
 * Local storage is a source of truth for cart items, not memory:
 * Before making changes to cart items (add/qty_change/remove), we get them from local storage.
 */
@Service()
export class CartService {
  private readonly customerService = inject(CustomerService);
  private readonly api = inject(Api);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly productService = inject(ProductService);
  private readonly wishlistService = inject(WishlistService);
  private readonly promoCodeService = inject(PromoCodeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrderService);
  private readonly deviceService = inject(DeviceService);

  private readonly persistenceKey = 'kl.cart';
  private readonly crossTabChannelName = 'kl.cart';
  private crossTabChannel: BroadcastChannel | null = null;

  readonly cartItems = signal<PopulatedCartItemDto[]>([], { equal: (_a, _b) => false });
  readonly rawCartItemsCount = signal<number>(0); // This exists so count in minicart is visible immediately after page load. Updated on every `cartItems` change.
  readonly isLoading = signal<boolean>(true);
  readonly initCartError = signal<string | null>(null);
  readonly cartPrices = signal<OrderPricesDto>(null);
  readonly crossSellProductIds = signal<number[]>([]);
  readonly isCartInit = signal<boolean>(false);

  readonly products = signal<ProductListItemDto[]>([]); // TODO Handle products no longer in stock
  readonly productsLoading = signal<boolean>(false);
  readonly productsError = signal<string | null>(null);

  readonly inAndOutOfStockCartItemsSorted = computed<PopulatedCartItemDto[]>(() => {
    const cartItems = this.cartItems();
    const inStockItems: PopulatedCartItemDto[] = [];
    const outOfStockItems: PopulatedCartItemDto[] = [];
    for (const item of cartItems) {
      if (item.isInStock) {
        inStockItems.push(item);
      } else {
        outOfStockItems.push(item);
      }
    }
    return [
      ...inStockItems,
      ...outOfStockItems,
    ];
  });

  private productsFetchSubscription: Subscription = null;

  constructor() {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    this.migrateCartItems();
    this.initCrossTabSync();

    this.orderService.orderPlaced$.subscribe(() => this.resetCartOnOrderPlace());

    effect(() => {
      this.customerService.customer();

      untracked(() => {
        if (!this.isCartInit()) {
          return;
        }
        const mergedCartItems = this.handleCustomerCartItems();
        if (!mergedCartItems) {
          return;
        }
        this.setCart(mergedCartItems, true, true).subscribe();
      });
    });

    effect(() => {
      const cartItems = this.cartItems();
      untracked(() => {
        if (!this.isCartInit()) {
          return;
        }
        this.persistCartItems(cartItems);
        this.setProducts(cartItems);
        this.rawCartItemsCount.set(cartItems.length);
        this.initCartError.set(null);
      });
    });

    this.initCart();
  }

  private initCart(): void {
    const cartItems = this.getCartItemsFromPersistence();
    this.rawCartItemsCount.set(cartItems.length);
    this.setCart(cartItems, false, false)
      .pipe(finalize(() => this.isCartInit.set(true)))
      .subscribe({
        error: error => this.initCartError.set(resolveHttpErrorMessage(error)),
      });
  }

  isInCart(productId: number): boolean {
    return this.cartItems().some(item => item.productId === productId);
  }

  setCartItem(options: {
    productId: number,
    qty: number,
    additionalServiceIds?: number[],
    analyticsData: { sku: string; name: string; price: number; source: string; },
  }): Observable<CartResponseDto> {
    const cartItems: CartItemDto[] = this.getUpToDateCartItems();

    const existingItemIndex = cartItems.findIndex(item => item.productId === options.productId);
    if (existingItemIndex === -1) {
      cartItems.unshift({
        productId: options.productId,
        qty: options.qty,
        additionalServiceIds: options.additionalServiceIds || [],
      });
    } else {
      cartItems[existingItemIndex].qty = Math.max(options.qty, 1);
    }

    this.persistCartItems(cartItems); // To prevent conflicting state in case user changes cart during the request (e.g. in another tab)
    return this.setCart(cartItems, true, true)
      .pipe(
        tap(_ => {
          vibrate();
          this.googleAnalyticsService.addToCart(options.analyticsData);
        }),
      );
  }

  /**
   * Puts a list of items in the cart in one request, through the same merge a customer's saved cart
   * goes through: an item already in the cart keeps the larger of the two quantities.
   */
  addCartItems(
    cartItems: CartItemDto[],
    analyticsData: ProductAnalyticsData[],
  ): Observable<CartResponseDto> {
    const mergedCartItems = this.mergeCartItems(this.getUpToDateCartItems(), cartItems);

    this.persistCartItems(mergedCartItems); // To prevent conflicting state in case user changes cart during the request (e.g. in another tab)
    return this.setCart(mergedCartItems, true, true)
      .pipe(
        tap(_ => {
          vibrate();
          for (const data of analyticsData) {
            this.googleAnalyticsService.addToCart(data);
          }
        }),
      );
  }

  moveCartItemToWishlist(
    productId: number,
    analyticsData: ProductAnalyticsData,
  ): Observable<CartResponseDto> {
    this.wishlistService.addToWishlist(productId, analyticsData);
    return this.removeCartItem(productId, analyticsData);
  }

  removeCartItem(
    productId: number,
    analyticsData: ProductAnalyticsData,
  ): Observable<CartResponseDto> {
    const allCartItems: CartItemDto[] = this.getUpToDateCartItems();
    const restCartItems = allCartItems.filter(item => item.productId !== productId);

    this.persistCartItems(restCartItems); // To prevent conflicting state in case user changes cart during the request (e.g. in another tab)
    return this.setCart(restCartItems, true, true)
      .pipe(
        finalize(() => vibrate()),
        tap({
          next: _ => {
            this.googleAnalyticsService.removeFromCart(analyticsData);
          },
        }),
      );
  }

  private setCart(cartItems: CartItemDto[], isChange: boolean, broadcastUpdate: boolean): Observable<CartResponseDto> {
    const requestDto: CartRequestDto = {
      cartItems: cartItems,
      promoCodeName: this.promoCodeService.getPromoCode?.name,
      isChange: isChange,
    };

    this.isLoading.set(true);
    return this.api.invoke(initCartFn, { body: requestDto })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        catchError(err => {
          if (cartItems.length) {
            throw err;
          }
          return of({
            populatedCartItems: [],
            crossSellProductIds: [],
            cartPrices: {
              totalCost: 0,
              discountValue: 0,
              itemsCost: 0,
            },
          });
        }),
        tap({
          next: response => {
            this.cartItems.set(response.populatedCartItems);
            this.cartPrices.set(response.cartPrices);
            this.crossSellProductIds.set(response.crossSellProductIds);
            if (broadcastUpdate) {
              this.broadcastCartUpdated();
            }
          },
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  private resetCartOnOrderPlace(): void {
    const outOfStockCartItems: CartItemDto[] = this.cartItems().filter(item => !item.isInStock); // Keep out of stock items in cart for UX
    this.setCart(outOfStockCartItems, true, false).subscribe();
  }

  /**
   * @returns merged cart items if cart items were changed, null otherwise.
   */
  private handleCustomerCartItems(): CartItemDto[] | null {
    if (!this.customerService.customer()) {
      return null;
    }
    const customerCartItems: CartItemDto[] = this.customerService.customer()?.cartItems || [];
    const persistedCartItems: CartItemDto[] = this.getCartItemsFromPersistence();
    if (!this.areCartItemsDifferent(persistedCartItems, customerCartItems)) {
      return null;
    }
    const mergedCartItems = this.mergeCartItems(persistedCartItems, customerCartItems);
    this.persistCartItems(mergedCartItems);
    return mergedCartItems;
  }

  /**
   * Local storage is a source of truth for cart items, not memory:
   * Before making changes to cart items (add/qty_change/remove), we get them from local storage.
   */
  private getUpToDateCartItems(): CartItemDto[] {
    try {
      const persistedCartItems = this.getCartItemsFromPersistence();
      return persistedCartItems;
    } catch (error) {
      return this.cartItems();
    }
  }

  private persistCartItems(cartItems: CartItemDto[]): void {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }
    const cartItemsToPersist: CartItemDto[] = cartItems.map(item => {
      return {
        productId: item.productId,
        qty: item.qty,
        additionalServiceIds: item.additionalServiceIds || [],
      };
    });
    localStorage.setItem(this.persistenceKey, JSON.stringify(cartItemsToPersist));
  }

  private getCartItemsFromPersistence(): CartItemDto[]  {
    if (!this.deviceService.isPlatformBrowser()) {
      return [];
    }
    const persistedCartItems = localStorage.getItem(this.persistenceKey);
    if (!persistedCartItems) {
      return [];
    }
    return JSON.parse(persistedCartItems);
  }

  private initCrossTabSync(): void {
    if (!('BroadcastChannel' in window)) {
      return;
    }

    this.crossTabChannel = new BroadcastChannel(this.crossTabChannelName);
    this.crossTabChannel.onmessage = () => {
      this.refreshCartFromPersistenceIfNeeded();
    };

    this.destroyRef.onDestroy(() => {
      this.crossTabChannel?.close();
      this.crossTabChannel = null;
    });
  }

  private broadcastCartUpdated(): void {
    if (!this.crossTabChannel) {
      return;
    }
    try {
      this.crossTabChannel.postMessage({ type: 'cartUpdated' });
    } catch {
      // ignore cross-tab messaging errors
    }
  }

  private refreshCartFromPersistenceIfNeeded(): void {
    const persistedCartItems = this.getCartItemsFromPersistence();
    const currentCartItems = this.cartItems();
    const areCartItemsSame = persistedCartItems.length === currentCartItems.length
      && persistedCartItems.every(p => currentCartItems.some(c => c.productId === p.productId && c.qty === p.qty));
    if (areCartItemsSame) {
      return;
    }
    this.setCart(persistedCartItems, false, false).subscribe();
  }

  /**
   * [F, J, D, B, C] + [E, B, C] => [F, J, D, E, B, C]
   */
  private mergeCartItems(cartItems1: CartItemDto[], cartItems2: CartItemDto[]): CartItemDto[] {
    const mergedCartItems: CartItemDto[] = structuredClone(cartItems1);
    let pendingCartItems: CartItemDto[] = [];

    for (const cartItem2 of cartItems2) {
      const pendingCartItem = pendingCartItems.find(cartItem => cartItem.productId === cartItem2.productId);
      if (pendingCartItem) {
        pendingCartItem.qty = Math.max(pendingCartItem.qty, cartItem2.qty);
        continue;
      }

      const mergedCartItem = mergedCartItems.find(cartItem => cartItem.productId === cartItem2.productId);
      if (mergedCartItem) {
        mergedCartItem.qty = Math.max(mergedCartItem.qty, cartItem2.qty);

        if (pendingCartItems.length) {
          const anchorIndex = mergedCartItems.findIndex(cartItem => cartItem.productId === cartItem2.productId);
          mergedCartItems.splice(anchorIndex, 0, ...pendingCartItems);
          pendingCartItems = [];
        }
      } else {
        pendingCartItems.push(structuredClone(cartItem2));
      }
    }

    mergedCartItems.push(...pendingCartItems);

    return mergedCartItems;
  }

  private setProducts(cartItems: PopulatedCartItemDto[]): void {
    const cartProductIds = cartItems.map(item => item.productId);

    this.productsFetchSubscription?.unsubscribe();
    this.productsFetchSubscription = null;
    this.productsError.set(null);

    if (!cartProductIds.length) {
      this.products.set([]);
      this.productsLoading.set(false);
      return;
    }

    const areProductsSame = cartProductIds.length === this.products().length
      && this.products().every(product => cartProductIds.includes(product.id));
    if (areProductsSame) {
      return;
    }

    this.productsLoading.set(true);
    this.productsFetchSubscription = this.productService
      .fetchProductsByIds(cartProductIds, cartProductIds.length)
      .pipe(
        finalize(() => this.productsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          const sorted = response.data.sort(
            (a, b) => cartProductIds.indexOf(a.id) - cartProductIds.indexOf(b.id),
          );
          this.products.set(sorted);

          // Products missing from the response are deleted/disabled — remove their cart items
          const existingProductIds = new Set(response.data.map(p => p.id));
          const filteredCartItems = cartItems.filter(item => existingProductIds.has(item.productId));
          if (filteredCartItems.length !== cartItems.length) {
            this.cartItems.set(filteredCartItems);
          }
        },
        error: error => {
          this.productsError.set(resolveHttpErrorMessage(error));
        },
      });
  }

  private migrateCartItems(): void {
    const legacyStorageKey = 'cart-v2';
    const legacyCartItems = localStorage.getItem(legacyStorageKey);
    if (!legacyCartItems) {
      return;
    }

    const legacyCartItemsJson: {
      productId: number;
      qty: number;
    }[] = JSON.parse(legacyCartItems);
    if (!legacyCartItemsJson.length) {
      localStorage.removeItem(legacyStorageKey);
      return;
    }

    const migratedCartItems: CartItemDto[] = legacyCartItemsJson.map(item => {
      return {
        productId: item.productId,
        qty: item.qty,
        additionalServiceIds: [],
      };
    });

    const newCartItems: CartItemDto[] = this.getCartItemsFromPersistence();
    const mergedCartItems = this.mergeCartItems(newCartItems, migratedCartItems);
    localStorage.setItem(this.persistenceKey, JSON.stringify(mergedCartItems));
    localStorage.removeItem(legacyStorageKey);
  }

  private areCartItemsDifferent(cartItems1: CartItemDto[], cartItems2: CartItemDto[]): boolean {
    return cartItems1.length !== cartItems2.length
      || cartItems1.some(c1 => !cartItems2.some(c2 => c2.productId === c1.productId && c2.qty === c1.qty));
  }
}
